import {
	Box,
	Button,
	CircularProgress,
	Divider,
	IconButton,
	Snackbar,
	Stack,
	Typography,
} from '@mui/material';
import {AlertActions, AlertModal} from 'presentation/components';
import {HandleMessage} from 'presentation/components/HandleMessage';
import Icon from 'presentation/icons';
import {translations} from 'presentation/translations-new';
import React, {Fragment, useEffect} from 'react';
import {EditSession} from '../EditSession';
import {FooterCardSession} from '../FooterCardSession';
import {FormSession} from '../FormSession';
import type {UseTherapySessionsReturn} from './hooks/types';
import {calculateAndFormatTotalSessionsValue} from './utils';
import {TherapyTutorials} from '../TherapyTutorials';
import {TutorialStepsId} from '../TherapyTutorials/types';

const {
	sessionsQuantity,
	session: sessionLabel,
	addSession: addSessionLabel,
	quantity,
	total: totalLabel,
	saveSessionToProceed,
	removeModal,
	removeSessionLabel,
} = translations['pt-br'].privateFlow.refund.therapies;

export const TherapySessions = ({hookProps}: {hookProps: UseTherapySessionsReturn}) => {
	const {
		theme,
		isLoadingProcedures,
		isEditMode,
		openRemoveModal,
		errorState,
		readonlySessionsCount,
		sessionsWithUnsavedChanges,
		addNewSession,
		handleOpenRemoveModal,
		removeSession,
		duplicateSession,
		editSession,
		cancelEditSession,
		saveCurrentEditSession,
		watchedSessions,
		setReadonlySessionsCount,
		formContext,
		editingSessionIndex,
		updateProcedureMutation,
		editSessionRefs,
		selectedSessionIndex,
		handleCloseRemoveModal,
		deleteProcedureMutation,
		setErrorState,
		handleAddNewSessionForm,
		lastSequentialProcedure,
		handleValidator,
	} = hookProps;

	useEffect(() => {
		if (watchedSessions.length === 0) {
			handleAddNewSessionForm(0, lastSequentialProcedure);
			setReadonlySessionsCount(0);
		} else if (isEditMode) {
			const sessionsWithData = watchedSessions.filter(
				session => session?.serviceDate && session?.sessionValue,
			).length;
			setReadonlySessionsCount(sessionsWithData);
		}
	}, [watchedSessions.length, isEditMode]);

	const sessionQuantity = watchedSessions.filter(
		session => session?.serviceDate && session?.sessionValue,
	).length;

	return (
		<Fragment>
			<Stack gap={1}>
				<Typography component="h3" variant="alertTitle" marginBottom={0}>
					{sessionsQuantity}
				</Typography>

				{watchedSessions.map((fieldItem, index) => {
					const isLastSession = index === watchedSessions.length - 1;
					const isFirstSession = index === 0;
					const sessionData = watchedSessions[index];

					const isReadonly = isEditMode ? index < readonlySessionsCount : !isLastSession;
					const showOptionsMenu = isReadonly && watchedSessions.length > 1;

					const lastSessionError =
						formContext.getFieldState('sessions')?.error && isLastSession;

					const lastSessionErrorRoot =
						formContext.formState.errors?.sessions?.root && isLastSession;

					const hasUnsavedChangesError = sessionsWithUnsavedChanges.has(index);
					const showBorderError = hasUnsavedChangesError || lastSessionErrorRoot;

					return (
						<Fragment key={fieldItem.id}>
							<Box
								id={isFirstSession ? TutorialStepsId.FORM_SESSIONS : undefined}
								border={1}
								borderColor={showBorderError ? 'error.main' : 'other.outlineBorder'}
								borderRadius={1}
								overflow={'hidden'}>
								<Stack gap={1} padding={2} paddingTop={1}>
									<Stack
										direction="row"
										justifyContent="space-between"
										alignItems="center">
										<Typography
											variant="subtitle1"
											fontWeight={600}
											color="text.secondary">
											{sessionLabel} {index + 1}
										</Typography>
										{showOptionsMenu && editingSessionIndex !== index && (
											<IconButton
												aria-label={removeSessionLabel}
												title={removeSessionLabel}
												size="small"
												onClick={() => handleOpenRemoveModal(index ?? 0)}>
												<Icon
													name="delete-outline-filled"
													color={theme.colors.light.error.main}
													size={'20px'}
												/>
											</IconButton>
										)}
									</Stack>
									<Divider />
									{isReadonly ? (
										<>
											<EditSession
												ref={el => {
													editSessionRefs.current[index] = el;
												}}
												index={index}
												sessionData={sessionData}
												onValidation={handleValidator}
											/>
											{isFirstSession && <TherapyTutorials step="edit" />}
										</>
									) : (
										<FormSession index={index} control={formContext.control} />
									)}
								</Stack>
								{isReadonly && (
									<FooterCardSession
										isEdit={editingSessionIndex === index}
										isLoading={updateProcedureMutation.isPending}
										onCancel={() => cancelEditSession(index)}
										onDuplicate={() => duplicateSession(index)}
										onEdit={() => editSession(index)}
										onSave={() => saveCurrentEditSession(index)}
									/>
								)}
							</Box>
							{lastSessionError && (
								<Typography color="error.main" variant="caption">
									{formContext.getFieldState('sessions')?.error?.message ??
										formContext.getFieldState('sessions')?.error?.root?.message}
								</Typography>
							)}
							{hasUnsavedChangesError && (
								<Typography color="error.main" variant="caption">
									{saveSessionToProceed}
								</Typography>
							)}
						</Fragment>
					);
				})}

				<Button
					id={TutorialStepsId.FORM_ADD_SESSION}
					variant="outlined"
					onClick={() => addNewSession({})}
					fullWidth
					disabled={isLoadingProcedures}
					endIcon={
						isLoadingProcedures ? <CircularProgress size={16} color="inherit" /> : null
					}>
					{addSessionLabel}
				</Button>

				<Divider />

				<Stack direction="row" justifyContent="space-between" alignItems="center">
					<Typography variant="body1" fontWeight={600} color="text.secondary">
						{`${quantity}: ${sessionQuantity}`}
					</Typography>
					<Typography variant="body1" fontWeight={600} color="text.secondary">
						{totalLabel}: {calculateAndFormatTotalSessionsValue(watchedSessions)}
					</Typography>
				</Stack>
			</Stack>
			<TherapyTutorials step="initial" />
			<AlertModal
				footerContent={
					<AlertActions
						primaryAction={removeSession}
						secondaryAction={handleCloseRemoveModal}
						secondaryText={removeModal.secondaryActionText}
						primaryText={removeModal.primaryActionText}
						isLoading={deleteProcedureMutation.isPending}
						isDisabled={deleteProcedureMutation.isPending}
						isDisabledSecondary={deleteProcedureMutation.isPending}
						flexDirection="column-reverse"
					/>
				}
				headerTitle={removeModal.title}
				title={
					<HandleMessage
						message={removeModal.description2}
						variables={[`${sessionLabel} ${(selectedSessionIndex ?? 0) + 1}`]}
					/>
				}
				open={openRemoveModal}
				onClose={handleCloseRemoveModal}
				variant="info"
				customIconName="info-filled"
			/>
			<Snackbar
				onClose={() => setErrorState(null)}
				message={errorState?.message}
				open={!!errorState}
				anchorOrigin={{vertical: 'top', horizontal: 'right'}}
				autoHideDuration={5000}
			/>
		</Fragment>
	);
};
